/**
 * Enhanced audio generation hook with job queue status tracking
 *
 * This hook extends useAudioGeneration with queue status display capabilities.
 * It provides real-time updates on queue position and estimated completion time.
 */

import { useState } from 'react';
import { useJobStatus } from './useJobStatus';

interface UseAudioGenerationWithQueueResult {
  // Job status tracking
  jobId: string | null;
  jobStatus: ReturnType<typeof useJobStatus>['jobStatus'];
  isQueued: boolean;
  isProcessing: boolean;
  queuePosition?: number;
  estimatedTimeRemaining?: string | null;
  statusMessage?: string;
  progress: number;
}

/**
 * Hook to add queue status tracking to audio generation
 *
 * Usage:
 * ```tsx
 * const { generateAudio } = useAudioGeneration();
 * const queueStatus = useAudioGenerationWithQueue();
 *
 * // After generation starts, pass the jobId to track status
 * await generateAudio(...);
 * queueStatus.startTracking(jobId);
 * ```
 */
export const useAudioGenerationWithQueue = (): UseAudioGenerationWithQueueResult & {
  startTracking: (jobId: string) => void;
  stopTracking: () => void;
  reset: () => void;
} => {
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // Use the job status hook
  const {
    jobStatus,
    isQueued,
    isProcessing,
    isCompleted,
    queuePosition,
    progress,
    estimatedTimeRemaining,
    statusMessage
  } = useJobStatus({
    jobId: currentJobId,
    enabled: !!currentJobId,
    pollInterval: 2000,
    onCompleted: (result) => {
      console.log('✅ Job completed:', result);
      // Auto-stop tracking after completion
      setTimeout(() => setCurrentJobId(null), 3000);
    },
    onError: (error) => {
      console.error('❌ Job failed:', error);
      // Auto-stop tracking after error
      setTimeout(() => setCurrentJobId(null), 5000);
    }
  });

  /**
   * Start tracking a job
   */
  const startTracking = (jobId: string) => {
    console.log('🔍 Starting job status tracking for:', jobId);
    setCurrentJobId(jobId);
  };

  /**
   * Stop tracking current job
   */
  const stopTracking = () => {
    console.log('⏹️ Stopping job status tracking');
    setCurrentJobId(null);
  };

  /**
   * Reset tracking state
   */
  const reset = () => {
    setCurrentJobId(null);
  };

  return {
    jobId: currentJobId,
    jobStatus,
    isQueued,
    isProcessing,
    queuePosition,
    estimatedTimeRemaining,
    statusMessage,
    progress,
    startTracking,
    stopTracking,
    reset
  };
};
